import { Box, Button, CircularProgress, Popover, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

interface ChapterNotePopoverProps {
  open: boolean
  /** Click position the panel hangs off, in viewport coordinates. */
  anchorPosition: { top: number; left: number } | null
  direction: 'rtl' | 'ltr'
  /** Book title font, so the chapter name reads like it does in the sidebar. */
  fontFamily?: string
  chapterTitle: string
  note: string
  onClose: () => void
  onSave: (note: string) => Promise<void>
}

/**
 * The author's working note for one Chapter — free text, one field, saved as a whole.
 * Lives on `book.settings.chapters[].note`, so it is book structure rather than content:
 * nothing here reaches the manuscript or the reader.
 */
export default function ChapterNotePopover({
  open,
  anchorPosition,
  direction,
  fontFamily,
  chapterTitle,
  note,
  onClose,
  onSave,
}: ChapterNotePopoverProps) {
  const isRtl = direction === 'rtl'
  const [draft, setDraft] = useState(note)
  const [saving, setSaving] = useState(false)

  // Each opening reloads from the saved note — a draft left over from another
  // chapter (or an abandoned edit) must not be offered as this chapter's note.
  useEffect(() => {
    if (open) {
      setDraft(note)
    }
    // Keyed on `open` alone: a save that updates `note` mid-edit must not stomp typing.
  }, [open])

  const dirty = draft.trim() !== note.trim()

  const save = async () => {
    if (!dirty || saving) {
      return
    }
    setSaving(true)
    try {
      await onSave(draft)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
      slotProps={{
        paper: {
          dir: direction,
          elevation: 8,
          sx: {
            p: 1.75,
            width: 360,
            borderRadius: 2,
            border: '1px solid var(--border-subtle)',
            backgroundImage: 'none',
          },
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: fontFamily || 'SBL Hebrew',
          fontSize: '0.9rem',
          fontWeight: 700,
          mb: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {chapterTitle || (isRtl ? 'ללא כותרת' : 'Untitled chapter')}
      </Typography>

      <TextField
        autoFocus
        multiline
        minRows={5}
        maxRows={14}
        fullWidth
        size="small"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void save()
          }
          // The panel opens from inside the sortable TOC — keep its keystrokes local.
          e.stopPropagation()
        }}
        placeholder={isRtl ? 'הערות על הפרק…' : 'Notes on this chapter…'}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem', lineHeight: 1.6 } }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
        <Button size="small" variant="contained" disabled={!dirty || saving} onClick={() => void save()} sx={{ px: 2 }}>
          {saving ? <CircularProgress size={16} thickness={5} color="inherit" /> : isRtl ? 'שמור' : 'Save'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.68rem', opacity: 0.5 }}>{isRtl ? '⌘↵ לשמירה' : '⌘↵ to save'}</Typography>
      </Box>
    </Popover>
  )
}
